import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  FileText,
  DollarSign,
  CreditCard,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Printer,
  RefreshCw,
  Package,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomerInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isViewInvoiceOpen, setIsViewInvoiceOpen] = useState(false);
  
  const token = localStorage.getItem('nas_customer_token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [invoicesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/nas-customer/my-invoices?token=${token}`),
        axios.get(`${API}/api/nas-customer/my-account-summary?token=${token}`)
      ]);
      setInvoices(invoicesRes.data.invoices || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setIsLoading(false);
    }
  };

  const viewInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API}/api/nas-customer/my-invoices/${invoice.id}?token=${token}`);
      setSelectedInvoice(response.data);
      setIsViewInvoiceOpen(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const printInvoice = (invoice) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${invoice.invoice_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #16a34a; padding-bottom: 20px; }
            .header h1 { margin: 0; color: #16a34a; font-size: 28px; }
            .header p { margin: 5px 0; color: #666; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
            .info-box { padding: 15px; background: #f9fafb; border-radius: 8px; }
            .info-box h3 { margin: 0 0 10px 0; font-size: 12px; color: #6b7280; text-transform: uppercase; }
            .info-box p { margin: 5px 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
            th { background: #f9fafb; font-weight: 600; font-size: 12px; text-transform: uppercase; }
            .total-row { font-weight: bold; background: #f0fdf4; }
            .total-row td { font-size: 16px; }
            .status { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
            .status-paid { background: #dcfce7; color: #16a34a; }
            .status-partial { background: #fef3c7; color: #d97706; }
            .status-unpaid { background: #fee2e2; color: #dc2626; }
            .footer { text-align: center; color: #6b7280; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>NAS - Nenen's Agrivet Supply</h1>
            <p>Invoice</p>
          </div>
          
          <div class="info-grid">
            <div class="info-box">
              <h3>Invoice Details</h3>
              <p><strong>Invoice #:</strong> ${invoice.invoice_number}</p>
              <p><strong>Date:</strong> ${new Date(invoice.invoice_date).toLocaleDateString()}</p>
              <p><strong>Order:</strong> ${invoice.delivery_code}</p>
              <p><strong>Status:</strong> <span class="status status-${invoice.payment_status}">${invoice.payment_status?.toUpperCase()}</span></p>
            </div>
            <div class="info-box">
              <h3>Bill To</h3>
              <p><strong>${invoice.customer_name}</strong></p>
              <p>${invoice.customer_phone || ''}</p>
              <p>${invoice.customer_address || ''}</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th style="text-align:center">Quantity</th>
                <th style="text-align:right">Unit Price</th>
                <th style="text-align:right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.items?.map(item => `
                <tr>
                  <td>${item.product_name}</td>
                  <td style="text-align:center">${item.quantity} ${item.unit}</td>
                  <td style="text-align:right">₱${item.unit_price?.toLocaleString()}</td>
                  <td style="text-align:right">₱${item.total?.toLocaleString()}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td colspan="3"><strong>Total Amount</strong></td>
                <td style="text-align:right"><strong>₱${invoice.total_amount?.toLocaleString()}</strong></td>
              </tr>
              <tr>
                <td colspan="3">Amount Paid</td>
                <td style="text-align:right; color: #16a34a;">₱${invoice.amount_paid?.toLocaleString()}</td>
              </tr>
              <tr class="total-row">
                <td colspan="3"><strong>Balance Due</strong></td>
                <td style="text-align:right; color: ${invoice.balance > 0 ? '#dc2626' : '#16a34a'};">
                  <strong>₱${invoice.balance?.toLocaleString()}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
          
          <div class="footer">
            <p>Thank you for your business!</p>
            <p style="font-size: 12px;">For questions about this invoice, please contact us.</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700 border-green-200',
      partial: 'bg-amber-100 text-amber-700 border-amber-200',
      unpaid: 'bg-red-100 text-red-700 border-red-200'
    };
    const icons = {
      paid: <CheckCircle size={14} className="mr-1" />,
      partial: <Clock size={14} className="mr-1" />,
      unpaid: <AlertCircle size={14} className="mr-1" />
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
        {icons[status]}
        {status?.toUpperCase()}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">My Invoices</h1>
          <p className="text-slate-500">View your invoices and payment history</p>
        </div>
        <Button variant="outline" onClick={loadData} size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Account Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs font-medium">Total Invoiced</p>
                  <p className="text-2xl font-bold">₱{summary.summary?.total_invoiced?.toLocaleString()}</p>
                </div>
                <FileText size={32} className="text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs font-medium">Total Paid</p>
                  <p className="text-2xl font-bold">₱{summary.summary?.total_paid?.toLocaleString()}</p>
                </div>
                <CreditCard size={32} className="text-green-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={`bg-gradient-to-br ${summary.summary?.outstanding_balance > 0 ? 'from-red-500 to-red-600' : 'from-slate-400 to-slate-500'} text-white`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-xs font-medium">Outstanding</p>
                  <p className="text-2xl font-bold">₱{summary.summary?.outstanding_balance?.toLocaleString()}</p>
                </div>
                <DollarSign size={32} className="text-red-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs font-medium">Unpaid Invoices</p>
                  <p className="text-2xl font-bold">{summary.summary?.unpaid_invoices || 0}</p>
                </div>
                <AlertCircle size={32} className="text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Unpaid Invoices Alert */}
      {summary?.unpaid_invoices?.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-800 flex items-center gap-2 text-lg">
              <AlertCircle size={20} />
              Unpaid Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.unpaid_invoices.slice(0, 5).map(inv => (
                <div 
                  key={inv.id} 
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-50"
                  onClick={() => viewInvoice(inv)}
                >
                  <div className="flex items-center gap-3">
                    <FileText size={18} className="text-amber-600" />
                    <div>
                      <p className="font-mono font-semibold text-sm">{inv.invoice_number}</p>
                      <p className="text-xs text-slate-500">{inv.delivery_code}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-red-600">₱{inv.balance?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Due: ₱{inv.total_amount?.toLocaleString()}</p>
                  </div>
                  <ChevronRight size={18} className="text-slate-400" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Invoices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText size={20} className="text-green-600" />
            All Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-10">
              <FileText className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500">No invoices yet</p>
              <p className="text-sm text-slate-400">Invoices will appear here after orders are delivered</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map(invoice => (
                <div 
                  key={invoice.id} 
                  className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => viewInvoice(invoice)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        invoice.payment_status === 'paid' ? 'bg-green-100' :
                        invoice.payment_status === 'partial' ? 'bg-amber-100' : 'bg-red-100'
                      }`}>
                        <FileText size={20} className={
                          invoice.payment_status === 'paid' ? 'text-green-600' :
                          invoice.payment_status === 'partial' ? 'text-amber-600' : 'text-red-600'
                        } />
                      </div>
                      <div>
                        <p className="font-mono font-semibold">{invoice.invoice_number}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                          <Package size={14} />
                          <span>{invoice.delivery_code}</span>
                          <span>•</span>
                          <Calendar size={14} />
                          <span>{new Date(invoice.invoice_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg">₱{invoice.total_amount?.toLocaleString()}</p>
                        {invoice.balance > 0 && (
                          <p className="text-sm text-red-600">Balance: ₱{invoice.balance?.toLocaleString()}</p>
                        )}
                      </div>
                      {getStatusBadge(invoice.payment_status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Payments */}
      {summary?.recent_payments?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard size={20} className="text-green-600" />
              Recent Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.recent_payments.slice(0, 5).map((payment, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-green-600" />
                    <div>
                      <p className="font-semibold text-green-700">₱{payment.amount?.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">
                        {payment.payment_method} • {payment.invoice_number}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500">
                    {new Date(payment.recorded_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Invoice Modal */}
      <Dialog open={isViewInvoiceOpen} onOpenChange={setIsViewInvoiceOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-green-600" />
              Invoice {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex justify-center">
                {getStatusBadge(selectedInvoice.payment_status)}
              </div>
              
              {/* Invoice Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-slate-500 text-xs">Order</p>
                  <p className="font-mono font-semibold">{selectedInvoice.delivery_code}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-slate-500 text-xs">Date</p>
                  <p className="font-semibold">{new Date(selectedInvoice.invoice_date).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Items */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
                  Items
                </div>
                <div className="divide-y">
                  {selectedInvoice.items?.map((item, idx) => (
                    <div key={idx} className="p-3 flex justify-between">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-xs text-slate-500">{item.quantity} {item.unit} × ₱{item.unit_price?.toLocaleString()}</p>
                      </div>
                      <p className="font-semibold">₱{item.total?.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Amount</span>
                  <span className="font-bold text-lg">₱{selectedInvoice.total_amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Amount Paid</span>
                  <span className="font-semibold">₱{selectedInvoice.amount_paid?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className={selectedInvoice.balance > 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                    Balance Due
                  </span>
                  <span className={`font-bold text-lg ${selectedInvoice.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ₱{selectedInvoice.balance?.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Payment History */}
              {selectedInvoice.payments?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-600 mb-2">Payment History</p>
                  <div className="space-y-2">
                    {selectedInvoice.payments.map((payment, idx) => (
                      <div key={idx} className="p-3 bg-green-50 rounded-lg border border-green-200 text-sm">
                        <div className="flex justify-between">
                          <span className="font-semibold text-green-700">₱{payment.amount?.toLocaleString()}</span>
                          <span className="text-slate-500">{new Date(payment.recorded_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {payment.payment_method}
                          {payment.reference_number && ` • Ref: ${payment.reference_number}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => selectedInvoice && printInvoice(selectedInvoice)}>
              <Printer size={16} className="mr-2" />
              Print
            </Button>
            <Button variant="outline" onClick={() => setIsViewInvoiceOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerInvoices;
